import React, { useState } from 'react';
import {
  Container,
  Form,
  Row,
  Col,
  Toast,
  ToastContainer,
  Alert,
  Spinner,
} from 'react-bootstrap';
import './styles/customerSignIn.css';
import { useNavigate } from 'react-router-dom';
import { updatePassword } from './CustomerLoginPageApi';
import Button from '../../components/atoms/button/Button';

const VerifyEmail = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleSendOTP = () => {
    setShowToast(true);
  };

  const validateCode = (code) => {
    const codeRegex = /^\d{8}$/;
    return codeRegex.test(code);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 8) {
      setCode(value);
      if (errors.code) {
        setErrors(prev => ({ ...prev, code: '' }));
      }
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    if (errors.newPassword) {
      setErrors(prev => ({ ...prev, newPassword: '' }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const newErrors = {};

    if (!code) {
      newErrors.code = 'Verification code is required';
    } else if (!validateCode(code)) {
      newErrors.code = 'Verification code must be exactly 8 digits';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!validatePassword(newPassword)) {
      newErrors.newPassword = 'Minimum 8 characters. Use letters (A–Z, a–z), numbers (0–9), and only these symbols: @ $ ! % * ? &. No spaces allowed.';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        const passwordData = {
          activationCode: code,
          password: newPassword
        };

        const result = await updatePassword(passwordData);

        if (result.success) {
          setToastMessage('Password updated successfully! Redirecting to login...');
          setToastVariant('success');
          setShowToast(true);

          setTimeout(() => {
            navigate('/customer-login');
          }, 6000);
        } else {
          setToastMessage(result.error || 'Failed to update password');
          setToastVariant('danger');
          setShowToast(true);
        }

      } catch (error) {
        console.error('Error updating password:', error);
        setToastMessage('An error occurred. Please try again.');
        setToastVariant('danger');
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: '100vh', backgroundColor: '#666' }}
    >
      <Container
        className="bg-white rounded p-4 shadow"
        style={{ maxWidth: '500px' }}
      >
        <div className="d-flex justify-content-end mb-2">
          <button
            onClick={() => navigate('/customer-login')}
            className="btn-close"
            aria-label="Close"
          />
        </div>
        <h4 className="text-center mb-2">Set new password</h4>
        <p className="text-center text-muted mb-4">
          Please enter the verification code and set your new password
        </p>

        <Form onSubmit={handleVerify}>
          <Row className="align-items-center mb-3 g-2">
            <Col xs={12}>
              <Form.Control
                type="text"
                placeholder="Enter 8-digit verification code"
                value={code}
                onChange={handleCodeChange}
                isInvalid={!!errors.code}
                maxLength={8}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.code}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <Row className="align-items-center mb-3 g-2">
            <Col xs={12}>
              <Form.Control
                type="password"
                placeholder="Enter new Password"
                value={newPassword}
                onChange={handlePasswordChange}
                isInvalid={!!errors.newPassword}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <Row className="align-items-center mb-3 g-2">
            <Col xs={12}>
              <Form.Control
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                isInvalid={!!errors.confirmPassword}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            </Col>
          </Row>

           <div className="d-flex flex-md-row justify-content-end gap-2 mt-4 w-100">
          <Button
            type="submit"
            variant='active'
            disabled={isLoading || !code || !newPassword || !confirmPassword}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating Password...
              </>
            ) : (
              'Update Password'
            )}
          </Button>
          </div>
        </Form>
      </Container>

      <ToastContainer position="top-center" className="p-3">
        <Toast
          bg={toastVariant}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={6000}
          autohide
        >
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default VerifyEmail;
